import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, tokens } from "./api";
import type {
  Appointment,
  AppointmentEvent,
  PhotoKind,
  GarageSettings,
  BillingCycle,
  BillingPlan,
  BillingState,
  BillingSubscription,
  BillingType,
  CheckoutResult,
  Customer,
  CustomerImportResult,
  Me,
  Paginated,
  Professional,
  ProfessionalEvent,
  RevenueReport,
  Service,
  SiteTheme,
  Stats,
  Vehicle,
  WorkingHoursWeek,
} from "./types";

export const keys = {
  me: ["me"] as const,
  billing: ["billing"] as const,
  billingPlans: ["billing", "plans"] as const,
  myProfile: ["me", "professional"] as const,
  settings: ["settings"] as const,
  workingHours: ["working-hours"] as const,
  site: ["site"] as const,
  stats: ["stats"] as const,
  services: ["services"] as const,
  professionals: ["professionals"] as const,
  customers: (search: string) => ["customers", search] as const,
  vehicles: (f: VehicleFilters) => ["vehicles", f] as const,
  revenueReport: (p: Period) => ["reports", "revenue", p] as const,
  appointments: (f: AppointmentFilters) => ["appointments", f] as const,
  appointmentEvents: (id: number) => ["appointments", id, "events"] as const,
  availability: (p: AvailabilityParams) => ["availability", p] as const,
};

export interface AppointmentFilters {
  date_from?: string;
  date_to?: string;
  professional?: number | "";
  status?: string;
}

export interface VehicleFilters {
  /** Placa, marca, modelo ou nome do dono — a mesma caixa de busca. */
  search?: string;
  customer?: number;
}

export interface Period {
  date_from: string;
  date_to: string;
}

export interface AvailabilityParams {
  professional?: number;
  services?: number[];
  date?: string;
  days?: number;
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => (await api.get<Me>("/me/")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useStats() {
  return useQuery({
    queryKey: keys.stats,
    queryFn: async () => (await api.get<Stats>("/stats/")).data,
  });
}

/** Relatório do período. Só dono e gerente — o backend devolve 403 para o
 *  resto, então a rota e o item de menu ficam atrás de `see_financials`.
 *
 * `staleTime` alto de propósito: relatório de período fechado não muda
 * enquanto o dono olha, e o refetch ao focar a aba (default do app, que
 * existe por causa da agenda) só custaria uma varrida de itens à toa.
 */
export function useRevenueReport(period: Period) {
  return useQuery({
    queryKey: keys.revenueReport(period),
    queryFn: async () =>
      (await api.get<RevenueReport>("/reports/revenue/", { params: period })).data,
    staleTime: 5 * 60_000,
    // Trocar de período não pode piscar a tela inteira: os cartões e as
    // tabelas continuam mostrando o período anterior até o novo chegar.
    placeholderData: (prev) => prev,
  });
}

export function useServices() {
  return useQuery({
    queryKey: keys.services,
    queryFn: async () => (await api.get<Service[]>("/services/")).data,
  });
}

export function useProfessionals() {
  return useQuery({
    queryKey: keys.professionals,
    queryFn: async () => (await api.get<Professional[]>("/professionals/")).data,
  });
}

export function useCustomers(search: string) {
  return useQuery({
    queryKey: keys.customers(search),
    queryFn: async () =>
      (await api.get<Paginated<Customer>>("/customers/", { params: { search } })).data,
    // Busca por telefone enquanto digita: sem isso a lista pisca a cada tecla.
    placeholderData: (prev) => prev,
  });
}

/** A frota da oficina.
 *
 * Sem paginação como serviços e profissionais: a busca por placa filtra
 * no servidor, e quem abre a tela está procurando UM carro, não folheando
 * a lista.
 */
export function useVehicles(filters: VehicleFilters = {}) {
  return useQuery({
    queryKey: keys.vehicles(filters),
    queryFn: async () => (await api.get<Vehicle[]>("/vehicles/", { params: filters })).data,
    placeholderData: (prev) => prev,
  });
}

/** Tudo que já foi feito neste carro — a pergunta nº 1 do balcão. */
export function useVehicleHistory(id: number | null) {
  return useQuery({
    queryKey: ["vehicles", id, "history"],
    queryFn: async () => (await api.get<Appointment[]>(`/vehicles/${id}/history/`)).data,
    enabled: id !== null,
  });
}

/** Importação de clientes por CSV — a mesma rota serve prévia e gravação.
 *
 * O arquivo sobe duas vezes (uma para conferir, outra para confirmar) e
 * isso é de propósito: nada fica pendurado no servidor entre os dois
 * cliques. Ver `apps/api/customer_import.py`.
 *
 * Só invalida a lista quando gravou — a prévia não muda nada no banco.
 */
export function useImportCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, confirm }: { file: File; confirm: boolean }) => {
      const body = new FormData();
      body.append("file", file);
      body.append("confirm", String(confirm));
      return (await api.post<CustomerImportResult>("/customers/import/", body)).data;
    },
    onSuccess: (data) => {
      if (data.confirmed) qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useAppointments(filters: AppointmentFilters) {
  return useQuery({
    queryKey: keys.appointments(filters),
    queryFn: async () => {
      // Segue as páginas até o fim, e não é zelo preventivo: o
      // `PAGE_SIZE` do backend é 100 e a visão de mês pede 42 dias de
      // uma vez. Uma oficina com quatro profissionais passa de 100
      // atendimentos em menos de duas semanas — o resto do mês
      // simplesmente não vinha, e a grade mostrava as últimas semanas
      // vazias com um total menor do que o real. Sem erro, sem aviso, e
      // com a aparência de agenda folgada.
      const results: Appointment[] = [];
      let page = 1;
      for (;;) {
        const { data } = await api.get<Paginated<Appointment>>("/appointments/", {
          params: { ...filters, page },
        });
        results.push(...data.results);
        // `count` fecha o laço mesmo se o `next` vier errado: página que
        // não acrescenta nada não pode virar requisição infinita no
        // navegador de quem só queria ver o mês.
        if (!data.next || !data.results.length || results.length >= data.count) {
          return { count: data.count, next: null, previous: null, results };
        }
        page += 1;
      }
    },
  });
}

export function useAvailability(params: AvailabilityParams, enabled: boolean) {
  return useQuery({
    queryKey: keys.availability(params),
    enabled,
    queryFn: async () => {
      const { data } = await api.get<Record<string, string[]>>("/availability/", {
        params: {
          professional: params.professional,
          services: params.services?.join(","),
          date: params.date,
          days: params.days ?? 1,
        },
      });
      return data;
    },
    // A grade envelhece rápido: outra pessoa pode ter reservado o slot.
    staleTime: 30_000,
  });
}

/** Invalida tudo que a agenda influencia: lista, grade e indicadores. */
export function useInvalidateAgenda() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["appointments"] });
    qc.invalidateQueries({ queryKey: ["availability"] });
    qc.invalidateQueries({ queryKey: keys.stats });
  };
}

export function useAppointmentAction() {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: string }) =>
      (await api.post<Appointment>(`/appointments/${id}/${action}/`)).data,
    onSuccess: invalidate,
  });
}

/** Laudo, anotação interna e valor orçado — o que se edita com o carro
 *  no box. Situação NÃO passa por aqui: transição é `useAppointmentAction`,
 *  que é quem o backend registra no log de auditoria. */
export function useUpdateAppointment() {
  const invalidate = useInvalidateAgenda();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      (await api.patch<Appointment>(`/appointments/${id}/`, body)).data,
    onSuccess: (_data, variables) => {
      invalidate();
      // O valor orçado vira linha de auditoria no backend; sem isto o
      // histórico aberto na tela continuaria mostrando o estado anterior.
      qc.invalidateQueries({ queryKey: keys.appointmentEvents(variables.id) });
    },
  });
}

/** Histórico do atendimento. `enabled` porque só quem abre o acordeão
 *  precisa — carregar junto da agenda seria uma chamada por linha. */
export function useAppointmentEvents(id: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.appointmentEvents(id),
    enabled,
    queryFn: async () => (await api.get<AppointmentEvent[]>(`/appointments/${id}/events/`)).data,
  });
}

/** Sobe uma foto da vistoria. Multipart e não JSON: é arquivo.
 *
 *  O axios monta o boundary sozinho a partir do FormData — passar um
 *  Content-Type à mão aqui é o erro clássico que faz o Django receber
 *  um corpo que ele não consegue separar. */
export function useUploadPhoto() {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: async ({
      id,
      file,
      kind,
      caption,
    }: {
      id: number;
      file: File;
      kind: PhotoKind;
      caption?: string;
    }) => {
      const body = new FormData();
      body.append("image", file);
      body.append("kind", kind);
      if (caption) body.append("caption", caption);
      return (await api.post(`/appointments/${id}/photos/`, body)).data;
    },
    onSuccess: invalidate,
  });
}

export function useDeletePhoto() {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: async ({ id, photoId }: { id: number; photoId: number }) =>
      (await api.delete(`/appointments/${id}/photos/${photoId}/`)).data,
    onSuccess: invalidate,
  });
}

export function useCreateAppointment() {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await api.post<Appointment>("/appointments/", payload)).data,
    onSuccess: invalidate,
  });
}

/** Bloqueio de agenda ("vou sair às 15h"). Precisa ser 2 toques. */
export function useCreateTimeOff() {
  const invalidate = useInvalidateAgenda();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      professional: number;
      starts_at: string;
      ends_at: string;
      reason?: string;
      all_day?: boolean;
    }) => (await api.post("/time-off/", body)).data,
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["time-off"] });
    },
  });
}

export function useTimeOff(dateFrom: string) {
  return useQuery({
    queryKey: ["time-off", dateFrom],
    queryFn: async () =>
      (await api.get("/time-off/", { params: { date_from: dateFrom } })).data,
  });
}

function useCrud<T>(resource: string, key: readonly unknown[]) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  return {
    create: useMutation({
      mutationFn: async (body: Partial<T>) =>
        (await api.post<T>(`/${resource}/`, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: Partial<T> & { id: number }) =>
        (await api.patch<T>(`/${resource}/${id}/`, body)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: number) => api.delete(`/${resource}/${id}/`),
      onSuccess: invalidate,
    }),
  };
}

// ------------------------------------------------------ regras de agenda
export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: async () => (await api.get<GarageSettings>("/settings/")).data,
    staleTime: 60_000,
  });
}

/** Salva as regras da casa.
 *
 * JSON e não multipart: aqui não há arquivo nenhum, e mandar número em
 * FormData chegaria como string no backend.
 *
 * O que invalidar depois importa mais que o próprio PATCH. Estas regras
 * são lidas em três lugares que já estão em cache:
 *   · `/me/` — traz o subconjunto que monta a agenda E a permissão
 *     `see_all_agendas`, que decide um item do menu lateral;
 *   · `/availability/` — a grade muda na hora com antecedência, passo e
 *     horizonte;
 *   · a lista de agendamentos — `auto_confirm` muda o status de quem nasce.
 */
export function useSettingsUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: Partial<GarageSettings>) =>
      (await api.patch<GarageSettings>("/settings/", fields)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.settings, data);
      qc.invalidateQueries({ queryKey: keys.me });
      qc.invalidateQueries({ queryKey: ["availability"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

// ------------------------------------------------ horário de funcionamento
export function useWorkingHours() {
  return useQuery({
    queryKey: keys.workingHours,
    queryFn: async () => (await api.get<WorkingHoursWeek>("/working-hours/")).data,
    staleTime: 60_000,
  });
}

/** Troca a semana inteira de uma vez.
 *
 * PUT e não PATCH porque é isso que o endpoint faz: apaga as faixas da
 * unidade e grava as que vieram. Salvar dia a dia daria sete requisições
 * e estados intermediários em que a oficina fica sem horário nenhum —
 * e é nesse intervalo que a página pública responde "sem horários".
 *
 * A grade de disponibilidade é a consequência imediata: o cliente que
 * está com a página aberta precisa parar de ver 18:30 no mesmo instante
 * em que a oficina passou a fechar às 18:00. O assistente de configuração
 * também olha para isto — o passo "horários" fica verde por causa dele.
 */
export function useWorkingHoursUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (semana: {
      unit: number | null;
      days: { weekday: number; ranges: [string, string][] }[];
    }) => (await api.put<WorkingHoursWeek>("/working-hours/", semana)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.workingHours, data);
      qc.invalidateQueries({ queryKey: ["availability"] });
      qc.invalidateQueries({ queryKey: ["onboarding"] });
    },
  });
}

// --------------------------------------------------------------- meu site
export function useSite() {
  return useQuery({
    queryKey: keys.site,
    queryFn: async () => (await api.get<SiteTheme>("/site/")).data,
    staleTime: 60_000,
  });
}

/** Salva a identidade do site.
 *
 * Sempre multipart, mesmo quando só mudou uma checkbox: o formulário é
 * um só e misturar dois content-types daria dois caminhos de erro para
 * manter. `logo`/`cover` só entram no corpo quando o dono escolheu um
 * arquivo novo — mandar o campo vazio APAGARIA a imagem existente.
 */
export function useSiteUpdate() {
  const qc = useQueryClient();
  return useMutation({
    // Omit em logo/cover: no GET eles são a URL (string), no PATCH são o
    // arquivo. Sem separar, o tipo do formulário briga com o da resposta.
    mutationFn: async (
      fields: Partial<Omit<SiteTheme, "logo" | "cover">> & { logo?: File; cover?: File },
    ) => {
      const form = new FormData();
      for (const [chave, valor] of Object.entries(fields)) {
        if (valor === undefined || valor === null) continue;
        form.append(chave, valor instanceof File ? valor : String(valor));
      }
      return (await api.patch<SiteTheme>("/site/", form)).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.site, data);
      // O nome da oficina aparece no menu lateral, que vem do /me/.
      qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Apaga logo ou capa. Ver o comentário do DELETE em apps/api/views.py. */
export function useSiteImageRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (field: "logo" | "cover") =>
      (await api.delete<SiteTheme>("/site/", { params: { field } })).data,
    onSuccess: (data) => qc.setQueryData(keys.site, data),
  });
}

/** Foto do profissional — arquivo, então multipart e não o CRUD JSON. */
export function useProfessionalPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, photo }: { id: number; photo: File }) => {
      const form = new FormData();
      form.append("photo", photo);
      return (await api.patch<Professional>(`/professionals/${id}/`, form)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.professionals }),
  });
}

// ------------------------------------------------------- colaborador
/** O cadastro do colaborador na visão dele — não precisa saber o id. */
export function useMyProfile(enabled = true) {
  return useQuery({
    queryKey: keys.myProfile,
    enabled,
    retry: false, // 404 = conta sem perfil de profissional; insistir não resolve
    queryFn: async () => (await api.get<Professional>("/me/professional/")).data,
  });
}

/**
 * Salva o próprio cadastro. Sempre multipart, porque a foto é o campo
 * que ele mais troca — e um formulário com dois content-types daria dois
 * caminhos de erro para manter (mesma decisão de `useSiteUpdate`).
 */
export function useMyProfileUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: Partial<Omit<Professional, "photo">> & { photo?: File }) => {
      const form = new FormData();
      for (const [chave, valor] of Object.entries(fields)) {
        // `undefined` é "não mexi neste campo"; string vazia é "apaguei",
        // e precisa ser enviada.
        if (valor === undefined || valor === null) continue;
        form.append(chave, valor instanceof File ? valor : String(valor));
      }
      return (await api.patch<Professional>("/me/professional/", form)).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.myProfile, data);
      qc.invalidateQueries({ queryKey: keys.professionals });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { current_password?: string; new_password: string }) => {
      const { data } = await api.post<{ access: string; refresh: string }>("/me/password/", body);
      // A troca invalida os tokens antigos no servidor; sem guardar os
      // novos, a requisição seguinte cairia em 401 e jogaria o
      // colaborador para o login logo depois de acertar a senha.
      tokens.set(data);
      return data;
    },
  });
}

/** Concessão e revogação de acesso — ações do dono na tela de Equipe. */
export function useProfessionalAccess() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.professionals });

  return {
    invite: useMutation({
      mutationFn: async ({ id, email }: { id: number; email: string }) =>
        (
          await api.post<{ email: string; expires_at: string; accept_url: string }>(
            `/professionals/${id}/invite/`,
            { email },
          )
        ).data,
      onSuccess: invalidate,
    }),
    setPassword: useMutation({
      mutationFn: async ({ id, email }: { id: number; email: string }) =>
        (
          await api.post<{ email: string; temp_password: string }>(
            `/professionals/${id}/set-password/`,
            { email },
          )
        ).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async (id: number) =>
        (await api.post<{ revoked: boolean }>(`/professionals/${id}/revoke-access/`)).data,
      onSuccess: invalidate,
    }),
  };
}

/** Histórico de alteração de CPF, Pix e comissão. Só quem vê financeiro. */
export function useProfessionalEvents(id: number | null) {
  return useQuery({
    queryKey: ["professional-events", id],
    enabled: id !== null,
    queryFn: async () => (await api.get<ProfessionalEvent[]>(`/professionals/${id}/events/`)).data,
  });
}

// ------------------------------------------------------------ assinatura
/** Estado de cobrança + faturas.
 *
 * `retry: false` e sem `staleTime`: esta tela costuma ser aberta logo
 * depois de pagar, e mostrar dado de cache aí significa mostrar "conta
 * bloqueada" para quem acabou de pagar.
 */
export function useBilling() {
  return useQuery({
    queryKey: keys.billing,
    queryFn: async () => (await api.get<BillingState>("/billing/subscription/")).data,
    retry: false,
    staleTime: 0,
  });
}

export function useBillingPlans() {
  return useQuery({
    queryKey: keys.billingPlans,
    queryFn: async () => (await api.get<{ plans: BillingPlan[] }>("/billing/plans/")).data.plans,
    staleTime: 30 * 60_000, // catálogo muda a cada meses, não a cada tela
  });
}

/** As três ações da tela de assinatura.
 *
 * Todas invalidam o `["me"]` junto: é de lá que sai a faixa de estado no
 * topo do painel, e pagar sem a faixa sumir parece que o pagamento não
 * pegou.
 */
export function useBillingActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: keys.billing });
    qc.invalidateQueries({ queryKey: keys.me });
  };

  return {
    saveProfile: useMutation({
      mutationFn: async (dados: { document?: string; legal_name?: string; email?: string }) =>
        (await api.patch<{ subscription: BillingSubscription }>("/billing/profile/", dados)).data,
      onSuccess: invalidate,
    }),
    checkout: useMutation({
      mutationFn: async (dados: {
        plan: string;
        cycle: BillingCycle;
        billing_type: BillingType;
      }) => (await api.post<CheckoutResult>("/billing/checkout/", dados)).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async (reason: string) =>
        (await api.post<{ subscription: BillingSubscription }>("/billing/cancel/", { reason }))
          .data,
      onSuccess: invalidate,
    }),
  };
}

export const useServiceCrud = () => useCrud<Service>("services", keys.services);
export const useProfessionalCrud = () =>
  useCrud<Professional>("professionals", keys.professionals);
export const useCustomerCrud = () => useCrud<Customer>("customers", ["customers"]);
export const useVehicleCrud = () => useCrud<Vehicle>("vehicles", ["vehicles"]);
